import React, { useState, useEffect, useMemo } from 'react';
import { SearchContainer, StyledTextField, SuggestionsList, SuggestionItem, ResultsContainer } from './styles';
import { getSearchResults } from '../../actions/searchResults';
import { Box, Divider, Typography, InputAdornment, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAutocomplete } from '../../actions/autocomplete';
import Fuse from 'fuse.js';
import SearchResult from '../SearchResults/SearchResult/SearchResult';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import viszz from '../../assets/viszz.png';
import InfoBoxes from './InfoBoxes';
import HowToUseDialog from './HowToUseCard';

const Search = () => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const autocompleteData = useSelector((state) => state.autocomplete);
  const [suggestions, setSuggestions] = useState([]);
  const [fuzzyFallback, setFuzzyFallback] = useState(false);
  const searchResults = useSelector((state) => state.searchResults);
  const [showResults, setShowResults] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setHasSelected(false);
    setShowResults(false);
  };

  // Fetch autocomplete data when the component mounts.
  useEffect(() => {
    dispatch(getAutocomplete());
  }, [dispatch]);

  // Debounce the input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 275);
    return () => clearTimeout(timer);
  }, [query]);

  // Build Fuse index once per data change (fuzzy fallback)
  const fuse = useMemo(() => {
    if (!autocompleteData?.length) return null;
    return new Fuse(autocompleteData, {
      keys: [
        { name: 'brandName',   weight: 0.7 }, // brand favored in fuzzy
        { name: 'genericName', weight: 0.3 },
      ],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [autocompleteData]);

  // Rank: prefix > brand contains > generic contains (prefix always wins)
  const rankAndTag = (items, q, words, mode = 'direct') => {
    const uniq = new Set();
    const out = [];

    for (const item of items) {
      const brand = (item.brandName || '').toLowerCase();
      const gen   = (item.genericName || '').toLowerCase();

      // Direct mode: every token must appear somewhere in brand OR generic
      // Fuzzy mode: Fuse already filtered, so keep everything
      const keep =
        mode === 'direct'
          ? words.every((w) => brand.includes(w) || gen.includes(w))
          : true;
      if (!keep) continue;

      const bPrefix  = q && brand.startsWith(q);
      const gPrefix  = q && gen.startsWith(q);
      const bContain = q && brand.includes(q);
      const gContain = q && gen.includes(q);

      // Bucket (lower is better)
      let cat = 5; // purely fuzzy fallback (no direct substring)
      if (bPrefix)       cat = 1;
      else if (gPrefix)  cat = 2;
      else if (bContain) cat = 3;
      else if (gContain) cat = 4;

      const matchedWith = (bPrefix || bContain) ? 'brand' : 'generic';

      // Deduplicate by brand|generic pair
      const key = `${item.brandName || ''}|${item.genericName || ''}`;
      if (uniq.has(key)) continue;
      uniq.add(key);

      out.push({ ...item, matchedWith, _cat: cat });
    }

    // Sort by bucket, then mild alphabetic tiebreakers
    out.sort(
      (a, b) =>
        a._cat - b._cat ||
        (a.brandName || '').localeCompare(b.brandName || '') ||
        (a.genericName || '').localeCompare(b.genericName || '')
    );
    return out;
  };

  // Build suggestions on query change (prefix-first direct, then fuzzy fallback)
  useEffect(() => {
    if (hasSelected) {
      setSuggestions([]);
      setFuzzyFallback(false);
      return;
    }

    const q = debouncedQuery.trim().toLowerCase();
    if (!q) {
      setSuggestions([]);
      setFuzzyFallback(false);
      return;
    }

    const words = q.split(/\s+/);

    // A) Direct pass (fast): requires all tokens present somewhere
    const directRanked = rankAndTag(autocompleteData, q, words, 'direct');
    if (directRanked.length) {
      setSuggestions(directRanked);
      setFuzzyFallback(false);
      return;
    }

    // B) Fuzzy fallback (Fuse), still ranked prefix-first when possible
    if (fuse) {
      const fuzzyItems = fuse.search(debouncedQuery, { limit: 25 }).map((r) => r.item);
      const fuzzyRanked = rankAndTag(fuzzyItems, q, words, 'fuzzy');
      setSuggestions(fuzzyRanked);
      setFuzzyFallback(true);
    } else {
      setSuggestions([]);
      setFuzzyFallback(false);
    }
  }, [debouncedQuery, autocompleteData, hasSelected, fuse]);

  // Helper to highlight matching tokens in the text.
  const highlightText = (text, tokens, color) => {
    if (!tokens || tokens.length === 0 || !text) return text;
    const lowerText = text.toLowerCase();
    let ranges = [];
    tokens.forEach(token => {
      if (!token) return;
      let startIndex = 0;
      const lowerToken = token.toLowerCase();
      while ((startIndex = lowerText.indexOf(lowerToken, startIndex)) !== -1) {
        ranges.push({ start: startIndex, end: startIndex + token.length });
        startIndex += token.length;
      }
    });
    if (ranges.length === 0) return text;

    // Merge overlapping ranges.
    ranges.sort((a, b) => a.start - b.start);
    const mergedRanges = [];
    ranges.forEach(range => {
      if (mergedRanges.length === 0) mergedRanges.push(range);
      else {
        const last = mergedRanges[mergedRanges.length - 1];
        if (range.start <= last.end) last.end = Math.max(last.end, range.end);
        else mergedRanges.push(range);
      }
    });

    const result = [];
    let lastIndex = 0;
    mergedRanges.forEach((range) => {
      if (lastIndex < range.start) result.push(text.substring(lastIndex, range.start));
      result.push(
        <span key={`hl-${range.start}-${range.end}`} style={{ color }}>
          {text.substring(range.start, range.end)}
        </span>
      );
      lastIndex = range.end;
    });
    if (lastIndex < text.length) result.push(text.substring(lastIndex));
    return result;
  };

  // Tokens for highlight rendering
  const queryTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  // When a suggestion is clicked, update the query and dispatch the search.
  const handleSuggestionClick = (suggestion) => {
    const displayName =
      suggestion.matchedWith === 'brand'
        ? `${suggestion.brandName} : ${suggestion.genericName}`
        : suggestion.genericName;

    const genericName = suggestion.genericName;

    setQuery(displayName);
    setSuggestions([]);
    setFuzzyFallback(false);
    setShowResults(true);
    setHasSelected(true);

    // Search API expects genericName
    dispatch(getSearchResults(genericName));
  };

  return (
    <Box
      sx={{
        width:{lg:'100%',}, marginLeft: {xs:'8%', sm:'2%', lg:0},
        p: {xs:0, md:2},
        backgroundColor: 'white',
        // borderRadius: 1,
      }}
    >
      <Box sx={{display:'flex', justifyContent:'space-between', alignItems: 'center', width:{xs:'80%', sm:'90%'}, margin:'auto', padding:'15px 0', paddingBottom:'32px'}}>
        <Typography variant="h3" sx={{fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    },  fontWeight:'600'}}>Search</Typography>
        <HowToUseDialog />
        {/* <SideEffectWidget /> */}
      </Box>

      <Box sx={{ display: 'flex', width: '100%', alignItems:'flex-start', flexDirection: {xs:'column', md:'row'} }}>
        <Box sx={{ flexBasis: { xs: '100%', md: '25%' } }} />

        {/* Middle Column (50%) */}
        <Box sx={{ flexBasis: { xs: '100%', md: '50%' },  width: {xs: '100%' }, marginTop: {xs:'10px', md:'0'} }}>
          <Box
            sx={{
              flexBasis: '50%', display:'flex', alignItems:'center', minHeight:'160px',
              padding:'0px 25px', margin: {xs:'0', md: '0 25px'}, boxSizing:'border-box',
              borderRadius:'16px', marginTop: 0, flexDirection: 'column', justifyContent: 'flex-start'
            }}
          >
            <div style={{display:'flex', alignItems:'center', flexDirection:'column', width:'100%'}}>
              <SearchContainer expanded={suggestions.length > 0} style={{display:'flex', flexDirection:'column', width:'100%'}}>
                <Box sx={{ width: 76, height: 76, borderRadius: '50%', background: '#e7efff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, margin:'auto' }}>
                  <SearchIcon sx={{ fontSize: 38, color: '#0A5FFF'}} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 4, textAlign:'center' }}>
                  Search for Medicines
                </Typography>
                <Typography variant="body2" sx={{ color: '#333', mt: 1, mb: 4, fontSize:'16px' }}>
                  Browse over 5000 NMRA registered medicines and discover alternative brands.
                </Typography>

                <StyledTextField
                  sx={{
                    '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' },
                  "& input": {
        color: "#333",
        fontSize: "1rem",
        fontWeight: 500,
        "&::placeholder": { color: "#888", opacity: 1,
          fontSize: { xs: '0.85rem', md: '1rem' },
         },
      }, },
                  }}
                  variant="outlined"
                  placeholder="Search for a medicine or brand..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowResults(false); // Hide results while editing
                    setHasSelected(false);
                  }}
                  showDropdown={suggestions.length > 0}
                  slotProps={{
                    input: {
                      style: {
                        color: '#333',
                        fontSize:'1rem',
                        fontWeight: '500',
                        fontFamily: 'Roboto',
                        background:'#f3f3f6',
                        border:'0 !important',
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: query && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{
                              padding: 0,
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              background:'white',
                              color:'#333'
                            }}
                          >
                            <CloseIcon style={{fontSize:'16px'}} />
                          </IconButton>
                        </InputAdornment>
                      )
                    },
                  }}
                />

                {suggestions.length > 0 && (
                  <SuggestionsList>
                    {fuzzyFallback && (
                      <div
                        style={{
                          display: 'flex',
                          backgroundColor: '#f1f1f1',
                          margin: 'auto',
                          justifyContent: 'center',
                          padding: '20px'
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          style={{ marginBottom: '8px', color: 'grey' }}
                        >
                          No direct matches, but we found some similar results:
                        </Typography>
                      </div>
                    )}

                    {suggestions.map((item, index) => (
                      <SuggestionItem key={index} onClick={() => handleSuggestionClick(item)}>
                        {item.matchedWith === 'brand' ? (
                          <>
                            <Typography variant="subtitle2" sx={{fontWeight:'bold'}}>
                              {highlightText(item.brandName, queryTokens, '#0bac0d')}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              style={{ fontSize: '13px', color: '#333' }}
                            >
                              {highlightText(item.genericName, queryTokens, '#0bac0d')}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="subtitle2">
                            {highlightText(item.genericName, queryTokens, '#3898ec')}
                          </Typography>
                        )}
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                )}
              </SearchContainer>

              {showResults && searchResults && searchResults.length > 0 && (
                <ResultsContainer style={{ marginTop: '15vh' }}>
                  <Typography variant="h5" style={{ textAlign: 'center' }}>
                    Results
                  </Typography>
                  <Divider
                    sx={{
                      width: '95%',
                      marginBottom: '20px',
                      mx: 'auto',
                      borderColor: '#ddd',
                    }}
                  />
                  {searchResults.map((result, index) => (
                    <SearchResult key={index} result={result} />
                  ))}
                </ResultsContainer>
              )}
            </div>
          </Box>

          <InfoBoxes />

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', color: '#aaa'
          }}>
            <img src={viszz} alt="Search illustration" style={{ height:'45vh', marginBottom: '16px', marginTop:'25px' }} />
          </div>
        </Box>

        {/* Right Column (25%) */}
        <Box sx={{ flexBasis: { xs: '100%', md: '25%' }, width: {xs:'100%'} }}>
          {/* Optional right column content */}
        </Box>
      </Box>
    </Box>
  );
};

export default Search;
